import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from '@/shared/store';
import { registerGlobalErrorHandlers } from './app/GlobalErrors';
import { completeStaleChunkRecovery } from './shared/utils/staleChunk';
import { configService } from './shared/lib/configService';
import './styles/global.css';
import i18n from './app/i18n';
import App from './app/App';
import { Toaster } from 'react-hot-toast';
import { TriangleAlert } from 'lucide-react';

registerGlobalErrorHandlers();

// Render the React app with auth configuration
function renderApp() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 6000,
              error: {
                icon: (
                  <div className="animate-shake">
                    <TriangleAlert
                      className="w-8 h-8"
                      style={{
                        fill: 'rgb(239, 68, 68)',
                        stroke: 'white',
                      }}
                      strokeWidth={2}
                    />
                  </div>
                ),
              },
            }}
          />
          <App />
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );

  // The bundle booted, so any stale-chunk recovery marker has done its job
  // (SP-1330). Drop it from the address bar.
  completeStaleChunkRecovery();
}

// Initialize and render the app
async function initializeApp() {
  try {
    // Wait for runtime configuration to load
    await configService.initialize();

    // Wait for i18n to be fully initialized before rendering
    await new Promise<void>((resolve) => {
      if (i18n.isInitialized) {
        resolve();
      } else {
        i18n.on('initialized', () => resolve());
      }
    });

    renderApp();
  } catch {
    // Render anyway with fallback config
    renderApp();
  }
}

// Start the app initialization
initializeApp();
